import { PapiAttachmentFile } from '@wix/da-papi-types';
import { toMediaSrcFile } from './helpers';
import { DeviationMediaItem, MediaSrcFile, MediaSrcSet, ThumbSetOptions } from './types';

class AttachmentMediaService {
  static defaultFullview = {
    type: 'unknown',
    src: '',
    width: 1000,
    height: 1000,
    isDefault: true,
  };

  isGif(attachment: PapiAttachmentFile): boolean {
    return attachment.type === 'gif';
  }

  isPng(attachment: PapiAttachmentFile): boolean {
    return attachment.type === 'png';
  }

  getMediaFile(attachment: PapiAttachmentFile, fileType: string): MediaSrcFile | undefined {
    const types = attachment?.media?.types;
    const item = types?.find(f => f.t === fileType);
    if (item) {
      return toMediaSrcFile(attachment, item);
    }
    return undefined;
  }

  getLargestFullview(attachment: PapiAttachmentFile): MediaSrcFile {
    const fvs = this._getFullviews(attachment);
    if (fvs.length === 0) {
      return AttachmentMediaService.defaultFullview;
    }

    const fv = fvs
      .filter((item: DeviationMediaItem) => item.t !== 'social_preview')
      .reduce((prev: DeviationMediaItem, curr: DeviationMediaItem) => {
        if ((prev.w || 0) > (curr.w || 0) || (prev.h || 0) > (curr.h || 0)) {
          return prev;
        }
        return curr;
      });

    return (
      (fv && toMediaSrcFile(attachment, fv as DeviationMediaItem)) ||
      AttachmentMediaService.defaultFullview
    );
  }

  getSrcSetForAtttachmentPreview(
    attachment: PapiAttachmentFile,
    isMobile: boolean,
    screenVw: string
  ): MediaSrcSet {
    // Use thumbs etc that do not crop
    const thumbTypes: string[] = ['375W', '414W', '750W', '828W'];

    // Get all available images, excluding fullview
    const images = this._getFilteredMediaTypes(attachment, {
      allowFullview: false,
      allowPreview: true,
      thumbTypes,
    });

    // Use the fullview as src, the srcset will make this not render
    const isGif = this.isGif(attachment);
    const isPng = this.isPng(attachment);
    const src = this.getLargestFullview(attachment);
    const preview = this.getMediaFile(attachment, 'preview');
    const fullview = this.getMediaFile(attachment, 'fullview');

    if (!src || !preview) {
      throw new Error('Cannot render a fullview without a fullview image');
    }

    // GIF is special, we don't know if they are animated so need to use the fullview
    // however transparent gifs will get all gif thumbs/preview so when that happens
    // we don't apply this special handling
    // return attribs so that we end up building empty srcset
    if (isGif && this._doesGifHaveStaticPreview(attachment)) {
      return {
        sizes: '',
        src: src.src,
        srcSet: `${preview.src} 10000w`,
        srcFile: src,
        previewFile: preview,
      };
    }

    // Special handling of PNGS
    // Some pixel artists use tiny apng and like gifs we have no idea
    // if the png is animated or not and pngs can be very very large
    // so we cap to ~200k and use static previews after that.
    if (isPng && fullview && fullview.filesize && fullview.filesize < 200 * 1000) {
      return {
        sizes: '',
        src: src.src,
        srcSet: `${fullview.src} 10000w`,
        srcFile: src,
        previewFile: fullview,
      };
    }

    // First grab all the embedded srcsets from the media files into
    // a flat array so we can include all the 2x versions
    const flatSet: any[] = [];
    for (const img of images) {
      const { ss, ...rest } = img;
      flatSet.push(rest);
      if (ss && Array.isArray(ss) && ss.length > 0) {
        for (const srcItem of ss) {
          flatSet.push({
            ...rest,
            t: `${rest.t}_x${srcItem.x}`,
            w: srcItem.w,
            h: srcItem.h,
            c: srcItem.c,
          });
        }
      }
    }

    const srcSet = flatSet
      // Dedupe by width
      .filter((v, i, a) => a.findIndex(v2 => v2.w === v.w) === i)
      // Sort small to large
      .sort((a, b) => {
        const asc = (a.w || 0) * (a.h || 0);
        const bsc = (b.w || 0) * (b.h || 0);
        if (asc === bsc) {
          return 0;
        }
        return asc < bsc ? -1 : 1;
      })
      // Convert to media file
      .map(file => toMediaSrcFile(attachment, file)!)
      // Create the srcset strings
      .map((mediaFile, index, stack) => {
        // Last item gets the src width * 2, this hopefully forces browser
        // to choose from the srcset
        if (stack[index + 1] === undefined) {
          return `${mediaFile.src} ${(src.width || 0) * 2}w`;
        }
        return `${mediaFile.src} ${mediaFile.width || 0}w`;
      });

    return {
      sizes: screenVw,
      src: src.src ?? '',
      srcSet: srcSet.join(', '),
      srcFile: src,
      previewFile: preview,
    };
  }

  getThumbSet(attachment: PapiAttachmentFile, options?: ThumbSetOptions): MediaSrcFile[] {
    const thumbTypes = this._getFilteredMediaTypes(attachment, options);
    return thumbTypes.map(file => toMediaSrcFile(attachment, file)!);
  }

  _getFullviews(attachment: PapiAttachmentFile) {
    const files = attachment.media?.types;
    if (!files) {
      return [];
    }

    // for gif images only the gif file is a valid fullview
    if (this.isGif(attachment)) {
      return files.filter(file => file && file.t.startsWith('fullview'));
    }

    return files.filter(file => file && /^fullview|preview|original/.test(file.t));
  }

  _getFilteredMediaTypes(attachment: PapiAttachmentFile, options?: ThumbSetOptions) {
    let thumbFileTypes = [
      '150',
      '200H',
      '300W',
      '200T',
      '250T',
      '350T',
      '400T',
      '375W',
      '414W',
      '750W',
      '828W',
    ];

    if (options?.thumbTypes) {
      thumbFileTypes = options.thumbTypes;
    }

    // Check if this deviation may be compressed by the editor, in this
    // case we can have reduced files array and need to allow
    if (this._mightBeCompressedFilesList(attachment)) {
      thumbFileTypes.push('preview', 'fullview', 'gif');
    }

    if (options && options.allowPreview) {
      thumbFileTypes.push('preview');
    }
    if (options && options.allowFullview) {
      thumbFileTypes.push('fullview');
    }
    if (options && options.allowSquareCrop) {
      thumbFileTypes.push('92S');
      thumbFileTypes.push('125S');
    }
    if (!attachment.media?.types) {
      return [];
    }
    return attachment.media.types.filter(file => thumbFileTypes.includes(file.t));
  }

  _doesGifHaveStaticPreview(attachment: PapiAttachmentFile) {
    const thumb = this.getMediaFile(attachment, '250T');
    return thumb && !/\.gif\?/.test(thumb.src);
  }

  _mightBeCompressedFilesList(attachment: PapiAttachmentFile) {
    if (!attachment.media?.types) {
      return false;
    }

    // Assume single file whatever it is to be safe
    if (attachment.media.types.length === 1) {
      return true;
    }

    const types: string[] = attachment.media.types.map(f => f.t);

    // social_preview bug T38231
    if (types.length === 2 && types.includes('fullview') && types.includes('social_preview')) {
      return true;
    }

    return false;
  }
}

export default new AttachmentMediaService();
